import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { SharePopover } from "@/components/SharePopover";
import { Separator } from "@/components/ui/separator";
import { getDeviceHash } from "@/lib/device";
import {
  Mail, Copy, Check, Key, ArrowRight, ArrowLeft, Shield,
  Archive, Trash2, Paperclip, Clock, MailOpen, AtSign, AlertTriangle, Reply, Forward,
  Crown, Lock, Sparkles, X, Eye, Send, PenLine,
  Download, FileText, RefreshCw, MoreVertical, UserPlus, ChevronDown, ChevronUp,
} from "lucide-react";
import ComposeEmail from "@/components/ComposeEmail";
import SendLimitBanner from "@/components/SendLimitBanner";
import AliasRules from "@/components/AliasRules";
import AliasActivity from "@/components/AliasActivity";
import NotificationSettings from "@/components/NotificationSettings";
import AccessCodeModal from "@/components/AccessCodeModal";
import strings from "@/lib/strings";
import { shareOrDownloadFile } from "@/lib/attachments";
import { formatDistanceToNow, format } from "date-fns";
import DocumentHead from "@/components/DocumentHead";

/**
 * Parse a UTC ISO timestamp string robustly across all browsers.
 * Handles Python's 6-decimal-place microseconds that some browsers (Firefox)
 * may fail to parse correctly with the timezone suffix.
 */
function parseUTC(dateStr) {
  if (!dateStr) return new Date();
  // If already ends with Z or +00:00, Date constructor should handle it.
  // But some browsers fail with 6-decimal microseconds + timezone.
  // Normalize: truncate to 3 decimal places and ensure 'Z' suffix.
  let s = String(dateStr);
  // Remove any timezone suffix (+00:00, Z)
  s = s.replace(/[Zz]$/, "").replace(/\+00:?00$/, "");
  // Truncate microseconds to milliseconds (keep max 3 decimal places)
  s = s.replace(/(\.\d{3})\d*$/, "$1");
  // Append Z to force UTC interpretation
  if (!s.endsWith("Z")) s += "Z";
  return new Date(s);
}

export default function FreeAliasPage() {
  const { claimCode: urlCode } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: userAuthLoading } = useAuth();

  // ── Authenticated-user bypass ──
  // Free-alias routes are STRICTLY for anonymous, code-based access.
  // Any signed-in user landing here is redirected to their persistent inbox.
  useEffect(() => {
    if (userAuthLoading) return;
    if (!user) return;
    const isAdmin = user.role === "admin" || user.role === "superadmin";
    navigate(isAdmin ? "/housekeeping" : "/inbox", { replace: true });
  }, [user, userAuthLoading, navigate]);

  // Debug: log page load to diagnose redirect issues
  useEffect(() => {
    console.log("[FreeAliasPage] Mounted", {
      url: window.location.href,
      urlCode,
      locationState: location.state,
      cookies: document.cookie,
      localStorage_anon_inbox: localStorage.getItem("anon_inbox_session"),
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const justCreated = location.state?.justCreated;
  const createdAlias = location.state?.alias;
  const skipCreatedScreen = location.state?.skipCreatedScreen;

  // SIMPLIFIED FLOW (Apr 30 2026) — hide conversion banners, secondary
  // panels (rules / activity / notifications / storage / send-test tools),
  // and the inline upgrade card. Replaced with a single discreet upgrade
  // CTA "Use your own anonymous address" → /pricing. Set to false to
  // restore the full feature set.
  const SIMPLIFIED_FLOW = true;

  const [claimCode, setClaimCode] = useState(urlCode || null);
  const [alias, setAlias] = useState(createdAlias || null);
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loading, setLoading] = useState(!!urlCode && !justCreated);
  const [copied, setCopied] = useState(null);
  const [showCreated, setShowCreated] = useState(!!justCreated && !skipCreatedScreen);
  const [seeding, setSeeding] = useState(false);
  const [expired, setExpired] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [showAccessCode, setShowAccessCode] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [activeFolder, setActiveFolder] = useState("inbox");
  const [composeOpen, setComposeOpen] = useState(false);
  const [sendStatus, setSendStatus] = useState({ remaining: null, total: null, limitReached: false });
  const [storageInfo, setStorageInfo] = useState(null);
  const [keepForeverOpen, setKeepForeverOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const prevEmailIdsRef = useRef(new Set());
  const isVisibleRef = useRef(true);

  // PROMPT 027 — Conversion-driven inbox UX. We need to know whether the
  // user picked their alias themselves (custom) or whether the backend
  // generated a random one — this drives the upgrade prompt copy:
  //   custom  → "Keep your address" (positive, "secure it")
  //   random  → "This is temporary, replace it" (replace flow + modal)
  // Source of truth: navigation state from Landing OR localStorage hint.
  // Fallback: if the alias's local_part is short and human-readable
  // (<= 12 chars, has a vowel), we treat it as custom; otherwise random.
  // Heuristic only kicks in for tab-restored sessions where state is gone.
  const [aliasChoiceMode, setAliasChoiceMode] = useState(() => {
    const fromState = location.state?.aliasChoiceMode;
    if (fromState === "custom" || fromState === "random") return fromState;
    return null; // resolve after alias loads
  });

  // PROMPT 027 — "Use my own address" inline conversion flow.
  const [desiredAlias, setDesiredAlias] = useState("");
  const [desiredAliasError, setDesiredAliasError] = useState("");
  const [switchModalOpen, setSwitchModalOpen] = useState(false);

  // The separate secret access_code (only available at creation time, passed via state)
  const [secretAccessCode] = useState(location.state?.access_code || null);

  // Auth state for token-based inbox protection
  const [needsAuth, setNeedsAuth] = useState(false);
  // Conversion banner — collapsed by default to keep the inbox content-first.
  // Tap the pill to expand the full "Keep this address / Switch instead" UI.
  const [conversionExpanded, setConversionExpanded] = useState(false);
  const [authCode, setAuthCode] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");

  // Inbox token — stored in sessionStorage (tab-specific, not shared across tabs/browsers)
  const inboxTokenRef = useRef(
    location.state?.inbox_token || sessionStorage.getItem(`inbox_token_${urlCode}`) || null
  );

  const setInboxToken = useCallback((token, code) => {
    inboxTokenRef.current = token;
    if (token && code) {
      sessionStorage.setItem(`inbox_token_${code}`, token);
    }
  }, []);

  const getAuthHeaders = useCallback(() => {
    if (inboxTokenRef.current) {
      return { "X-Inbox-Token": inboxTokenRef.current };
    }
    return {};
  }, []);

  // Usage-based upgrade prompt tracking
  const [emailsViewed, setEmailsViewed] = useState(0);
  const hasReceivedEmail = emails.length > 0;
  const hasEngaged = emailsViewed >= 1;

  const fetchInbox = useCallback(async (code, silent = false, folder = "inbox") => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/free/inbox/${code}`, {
        headers: getAuthHeaders(),
        params: { folder },
      });
      setNeedsAuth(false);
      setAlias(data.alias);

      // P0 (May 1 2026) — Persist claim_code for BFCache/refresh recovery.
      // If iOS Safari discards our SPA state during a share/save round-trip,
      // this lets the next mount rehydrate the inbox instead of showing a
      // blank white screen (see the `!alias` recovery fallback below).
      try {
        if (data?.alias?.claim_code) {
          sessionStorage.setItem("qi_last_claim_code", data.alias.claim_code);
        }
      } catch { /* storage disabled — silent */ }

      // Detect new emails on silent poll (only for inbox folder)
      if (silent && folder === "inbox" && prevEmailIdsRef.current.size > 0) {
        const newOnes = (data.emails || []).filter((e) => !prevEmailIdsRef.current.has(e.email_id));
        if (newOnes.length > 0) {
          toast.success(newOnes.length === 1 ? "New email received" : `${newOnes.length} new emails received`, { duration: 3000 });
        }
      }
      if (folder === "inbox") {
        prevEmailIdsRef.current = new Set((data.emails || []).map((e) => e.email_id));
      }

      setEmails(data.emails);
      setExpired(data.expired || false);
      // Register device session for smart recovery
      if (data.alias && !silent) {
        getDeviceHash().then((dh) => {
          api.post("/free/device-session", { device_hash: dh, claim_code: code }).catch(() => {});
        });
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        if (!silent) setNeedsAuth(true);
      } else if (!silent) {
        if (status === 403) {
          toast.error("This email has been linked to an account. Please sign in.");
        } else {
          toast.error("Could not load inbox. Check your access code.");
        }
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const inboxReadyToastFiredRef = useRef(false);

  useEffect(() => {
    // If justCreated, the inbox_token was passed via location.state from create-alias
    if (justCreated && location.state?.inbox_token && urlCode) {
      setInboxToken(location.state.inbox_token, urlCode);
    }
    if (urlCode && !justCreated) fetchInbox(urlCode, false, activeFolder);
    // Direct entry: skip created screen, load inbox, show toast EXACTLY once
    // per mount. The effect re-runs when activeFolder/location.state change,
    // which would otherwise fire the toast multiple times.
    if (skipCreatedScreen && justCreated && urlCode && !inboxReadyToastFiredRef.current) {
      inboxReadyToastFiredRef.current = true;
      fetchInbox(urlCode, false, activeFolder);
      toast.success(strings.toasts.inboxReady, {
        id: "inbox-ready",
        duration: 6000,
      });
    }
  }, [urlCode, fetchInbox, justCreated, skipCreatedScreen, location.state, setInboxToken, activeFolder]);

  // P0 (May 1 2026) — Recovery after iOS Safari BFCache/share-sheet round
  // trip. If the user lands on `/free` (no URL claim code) with no alias
  // state, we have no way to render the inbox. Before falling back to a
  // recovery screen, try to redirect to the last-known claim_code persisted
  // during a successful `fetchInbox` call above. This recovers most iOS
  // "blank screen after download" cases without any user interaction.
  useEffect(() => {
    if (urlCode) return;        // URL already carries the code
    if (alias) return;          // we already have alias state
    if (loading) return;        // still fetching
    if (justCreated) return;    // creation flow in progress
    try {
      const stored = sessionStorage.getItem("qi_last_claim_code");
      if (stored) {
        navigate(`/free/${stored}`, { replace: true });
      }
    } catch { /* storage disabled — fall through to recovery UI */ }
  }, [urlCode, alias, loading, justCreated, navigate]);

  // 5-second auto-refresh polling (visibility-aware, stops when auth needed)
  useEffect(() => {
    const code = claimCode || urlCode;
    if (!code || needsAuth) return;
    const intervalId = setInterval(() => {
      if (isVisibleRef.current) fetchInbox(code, true, activeFolder);
    }, 5000);
    const handleVisibility = () => {
      isVisibleRef.current = !document.hidden;
      if (!document.hidden) fetchInbox(code, true, activeFolder);
    };
    // P0 fix (Apr 30 2026): iOS returns the SPA from its save/share
    // dialog via BFCache, which does NOT fire `visibilitychange`. Listen
    // to `pageshow` with `persisted === true` so we re-hydrate the inbox
    // when the dialog closes and avoid a blank state.
    const handlePageShow = (e) => {
      if (e.persisted) fetchInbox(code, true, activeFolder);
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pageshow", handlePageShow);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [claimCode, urlCode, fetchInbox, needsAuth, activeFolder]);

  useEffect(() => {
    if (claimCode && !urlCode) setShowCreated(true);
  }, [claimCode, urlCode]);

  // Fetch Nina send status for compose dialog
  const fetchSendStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/nina/status");
      setSendStatus({
        remaining: data.sends_remaining,
        total: data.sends_total,
        limitReached: !data.send_allowed,
      });
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { fetchSendStatus(); }, [fetchSendStatus]);

  // Fetch storage usage
  const fetchStorageInfo = useCallback(async () => {
    const code = claimCode || urlCode;
    if (!code) return;
    try {
      const { data } = await api.get(`/free/storage/${code}`, { headers: getAuthHeaders() });
      setStorageInfo(data);
    } catch { /* ignore */ }
  }, [claimCode, urlCode, getAuthHeaders]);
  useEffect(() => { fetchStorageInfo(); }, [fetchStorageInfo]);

  const handleFolderSwitch = (folder) => {
    setActiveFolder(folder);
    setSelectedEmail(null);
    setEmails([]);
  };

  const handleCopy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = document.createElement("textarea");
      el.value = text;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(id);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(null), 2000);
  };

  const handleSeedDemo = async () => {
    if (!claimCode) return;
    setSeeding(true);
    try {
      await api.post(`/free/seed-demo/${claimCode}`, {}, { headers: getAuthHeaders() });
      toast.success("Demo emails generated");
      fetchInbox(claimCode, false, activeFolder);
    } catch {
      toast.error("Failed to generate demo");
    } finally {
      setSeeding(false);
    }
  };

  const handleManualRefresh = async () => {
    const code = claimCode || urlCode;
    if (!code) return;
    setRefreshing(true);
    await fetchInbox(code, true, activeFolder);
    setRefreshing(false);
  };

  const handleSendTestEmail = async () => {
    if (!alias?.alias_address) return;
    setSendingTest(true);
    try {
      await api.post("/mail/inbound", {
        recipient: alias.alias_address,
        sender: "test@anonmail.app",
        sender_name: "QuickInbox",
        subject: "Your temporary email is ready",
        body_html: "<p>This email was delivered to your anonymous address <strong>" + alias.alias_address + "</strong>. Any email sent here will appear in your inbox instantly.</p>",
        body_text: "This email was delivered to your anonymous address " + alias.alias_address + ". Any email sent here will appear in your inbox instantly.",
      });
      toast.success("Test email delivered to your inbox!");
      if (claimCode) fetchInbox(claimCode, false, activeFolder);
    } catch {
      toast.error("Could not send test email");
    } finally {
      setSendingTest(false);
    }
  };

  const handleAction = async (emailId, action) => {
    if (!claimCode) return;
    // Reply / Forward require an authenticated PRO account — surface a
    // friendly toast on the trial flow instead of hitting the API and
    // leaving the user in a confused state.
    if (action === "reply" || action === "forward") {
      toast.info(
        action === "reply"
          ? "Reply is available on PRO. Upgrade to keep this address and reply from it."
          : "Forward is available on PRO. Upgrade to keep this address and forward emails."
      );
      return;
    }
    try {
      await api.post(`/free/action/${claimCode}/${emailId}`, { action }, { headers: getAuthHeaders() });
      if (action === "delete" || action === "archive") {
        setEmails((prev) => prev.filter((e) => e.email_id !== emailId));
        if (selectedEmail?.email_id === emailId) setSelectedEmail(null);
      } else {
        fetchInbox(claimCode, false, activeFolder);
      }
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("This action requires an account. Upgrade to access.");
      } else {
        toast.error("Action failed");
      }
    }
  };

  const handleSelectEmail = async (email) => {
    setSelectedEmail(email);
    setEmailsViewed((c) => c + 1);
    if (email.status === "unread" && claimCode) {
      try {
        await api.get(`/free/email/${claimCode}/${email.email_id}`, { headers: getAuthHeaders() });
        setEmails((prev) => prev.map((e) => e.email_id === email.email_id ? { ...e, status: "read" } : e));
      } catch { /* ignore */ }
    }
  };

  const handleAuth = async () => {
    const code = authCode.trim();
    if (!code) return;
    setAuthLoading(true);
    setAuthError("");
    try {
      // Send both claim_code (from URL) and access_code (user's secret)
      const currentCode = claimCode || urlCode;
      const { data } = await api.post("/free/auth", {
        claim_code: currentCode,
        access_code: code,
      });
      // Store the inbox token from response body
      if (data.inbox_token) {
        setInboxToken(data.inbox_token, currentCode);
      }
      setNeedsAuth(false);
      setClaimCode(currentCode);
      fetchInbox(currentCode, false, activeFolder);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Invalid access code";
      setAuthError(detail);
    } finally {
      setAuthLoading(false);
    }
  };

  // PROMPT 027 — every "upgrade" link now lands on /pricing. We pass the
  // current alias's local_part as a hint ONLY when the user actually
  // chose it (custom). Random aliases never get auto-bound to a paid
  // plan — that would push the pricing page into showing an ugly random
  // string the user never asked to keep.
  const upgradeUrl = (() => {
    if (aliasChoiceMode !== "custom") return "/pricing";
    const localPart = String(alias?.alias_address || "").split("@")[0];
    return localPart ? `/pricing?keep_alias=${encodeURIComponent(localPart)}` : "/pricing";
  })();

  // Real-time 30-minute countdown (updates every second)
  useEffect(() => {
    if (!alias?.claim_code_expires_at) return;
    const update = () => {
      const expiresAt = parseUTC(alias.claim_code_expires_at).getTime();
      const now = Date.now();
      const diff = expiresAt - now;
      if (diff <= 0) {
        setTimeRemaining({ text: "Expired", minutes: 0, seconds: 0, urgent: false, expired: true });
        setExpired(true);
        return;
      }
      const totalSeconds = Math.floor(diff / 1000);
      // Cap at 30 minutes (the actual TTL) — prevents stale/legacy aliases showing wrong values
      const cappedSeconds = Math.min(totalSeconds, 30 * 60);
      const minutes = Math.floor(cappedSeconds / 60);
      const seconds = cappedSeconds % 60;
      const mm = String(minutes).padStart(2, "0");
      const ss = String(seconds).padStart(2, "0");
      const urgent = minutes < 5;
      const critical = minutes < 1;
      const preExpiry = cappedSeconds <= 120; // < 2 minutes
      const lastSeconds = cappedSeconds <= 30; // < 30 seconds
      setTimeRemaining({ text: `${mm}:${ss}`, minutes, seconds, urgent, critical, preExpiry, lastSeconds, expired: false });
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [alias?.claim_code_expires_at]);

  // PROMPT 027 — Resolve aliasChoiceMode once the alias loads. Reads
  // localStorage hint set on Landing trial start; falls back to a tiny
  // heuristic so deep-linked / refreshed sessions still get the right
  // copy (custom vs random).
  useEffect(() => {
    if (!alias?.alias_address || aliasChoiceMode) return;
    const localPart = String(alias.alias_address).split("@")[0] || "";
    let resolved = null;
    try {
      const hint = localStorage.getItem("qib_trial_alias_choice") || "";
      if (hint.startsWith("custom:")) {
        const want = hint.slice("custom:".length);
        if (want && want === localPart.toLowerCase()) resolved = "custom";
      }
    } catch { /* private mode */ }
    if (!resolved) {
      // Heuristic: a "random" backend alias tends to be alphanumeric mush
      // (e.g. "43fruhu32577"), while user-chosen aliases usually contain
      // a vowel and are <= 14 chars. Conservative — bias toward "random"
      // when uncertain so the inbox keeps nudging upgrade.
      const hasVowel = /[aeiou]/i.test(localPart);
      const looksRandom = localPart.length >= 10 && /\d/.test(localPart) && /[a-z]/i.test(localPart);
      resolved = !looksRandom && hasVowel && localPart.length <= 14 ? "custom" : "random";
    }
    setAliasChoiceMode(resolved);
  }, [alias?.alias_address, aliasChoiceMode]);

  // PROMPT 027 — Validation for the "Use my own address" inline input.
  const validateDesiredAlias = useCallback((val) => {
    if (!val) return "";
    if (!/^[a-zA-Z0-9._-]+$/.test(val)) return "Letters, numbers, dots, hyphens, underscores only";
    if (/\.\./.test(val)) return "Dots can't be next to each other";
    if (/^[.\-_]|[.\-_]$/.test(val)) return "Can't start or end with a special char";
    if (val.length < 3) return "At least 3 characters";
    return "";
  }, []);

  const goToPricingWithAlias = useCallback((localPart) => {
    const safe = (localPart || "").toLowerCase().trim();
    try {
      if (safe) {
        localStorage.setItem("qib_pending_keep_alias", safe);
      }
      // PROMPT 030 → trial hold countdown. Persist the alias's hard
      // expiry (claim_code_expires_at, ISO) so the pricing page can
      // render a quiet "Reserved for you — mm:ss remaining" line under
      // the address chip. We deliberately reuse the same expiry the
      // inbox already enforces — no new clock to drift out of sync.
      const expIso = alias?.claim_code_expires_at;
      if (expIso) {
        localStorage.setItem("qib_pending_keep_alias_until", String(expIso));
      } else {
        localStorage.removeItem("qib_pending_keep_alias_until");
      }
    } catch { /* private mode */ }
    setSwitchModalOpen(false);
    navigate(safe ? `/pricing?keep_alias=${encodeURIComponent(safe)}` : "/pricing");
  }, [alias?.claim_code_expires_at, navigate]);

  const handleSecureCurrent = useCallback(() => {
    const localPart = String(alias?.alias_address || "").split("@")[0];
    goToPricingWithAlias(localPart);
  }, [alias?.alias_address, goToPricingWithAlias]);

  const handleOpenSwitchModal = useCallback(() => {
    const err = validateDesiredAlias(desiredAlias);
    if (err) { setDesiredAliasError(err); return; }
    setSwitchModalOpen(true);
  }, [desiredAlias, validateDesiredAlias]);

  const handleConfirmSwitch = useCallback(() => {
    goToPricingWithAlias(desiredAlias);
  }, [desiredAlias, goToPricingWithAlias]);

  const getExpirationInfo = () => {
    if (!alias?.claim_code_expires_at) return null;
    const expiresAt = parseUTC(alias.claim_code_expires_at);
    const now = new Date();
    if (now > expiresAt) return { text: "Expired", urgent: true };
    const totalMin = Math.ceil((expiresAt - now) / 60000);
    if (totalMin <= 5) return { text: `Expires in ${totalMin} min`, urgent: true };
    return { text: `${totalMin} min remaining`, urgent: false };
  };

  // Just-Created screen (only shown if NOT skipCreatedScreen)
  if (showCreated && alias) {
    const expInfo = getExpirationInfo();
    return (
      <>
      <DocumentHead
        title="Get a Free Anonymous Email — QuickInbox"
        description="Generate a disposable email address in one click. Perfect for sign-ups, trials, and keeping your real address private."
        path="/free"
      />
      <div className="min-h-screen bg-white flex items-center justify-center px-4" data-testid="free-alias-created">
        <div className="max-w-md w-full space-y-6 text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-green-50 flex items-center justify-center">
            <Check size={28} className="text-green-600" />
          </div>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium text-[#111111]" data-testid="created-title">Deine Inbox ist bereit.</h1>
            <p className="text-sm text-zinc-500 mt-2">Du kannst sofort Emails empfangen.</p>
          </div>

          <div className="p-4 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5] text-left space-y-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Your email address</p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#555555] flex-1 truncate" data-testid="created-alias-address">{alias.alias_address}</span>
                <button onClick={() => handleCopy(alias.alias_address, "addr")} className="text-zinc-500 hover:text-[#555555]" data-testid="copy-alias-address">
                  {copied === "addr" ? <Check size={15} className="text-green-600" /> : <Copy size={15} />}
                </button>
              </div>
            </div>
            <Separator />
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Zugangscode</p>
              <div className="flex items-center gap-2">
                <code className="text-base font-mono font-semibold text-[#111111] tracking-wider" data-testid="created-access-code">{secretAccessCode || "—"}</code>
                <button onClick={() => handleCopy(secretAccessCode, "code")} className="text-zinc-500 hover:text-[#555555]" data-testid="copy-access-code">
                  {copied === "code" ? <Check size={15} className="text-green-600" /> : <Copy size={15} />}
                </button>
              </div>
              {expInfo && (
                <p className={`text-[10px] font-mono mt-1 ${expInfo.urgent ? "text-amber-600" : "text-zinc-500"}`}>
                  Valid for {expInfo.text}
                </p>
              )}
            </div>
          </div>

          <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-left" data-testid="access-code-hint">
            <p className="text-xs text-amber-800 flex items-start gap-2">
              <Key size={14} className="shrink-0 mt-0.5 text-amber-600" />
              <span><strong>Important:</strong> Save this access code now! It will only be shown once and is required to access your inbox from another browser or device.</span>
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <Button
              onClick={() => { setShowCreated(false); const c = urlCode || claimCode; if (c) fetchInbox(c, false, "inbox"); }}
              className="w-full h-11 bg-violet-600 hover:bg-violet-500 text-white transition-all hover:-translate-y-0.5 hover:shadow-md"
              data-testid="go-to-inbox-button"
            >
              Inbox öffnen <ArrowRight size={16} className="ml-2" />
            </Button>
            <Link to={upgradeUrl} data-testid="created-upgrade-link">
              <Button variant="outline" className="w-full h-11 border-blue-200 text-blue-600 hover:bg-blue-50">
                <Shield size={14} className="mr-2" /> Upgrade & keep this inbox
              </Button>
            </Link>
          </div>
        </div>
      </div>
      </>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Auth required screen — shown when inbox_token cookie is missing/invalid (anonymous only)
  if (needsAuth) {
    return (
      <>
      <DocumentHead
        title="Get a Free Anonymous Email — QuickInbox"
        description="Generate a disposable email address in one click. Perfect for sign-ups, trials, and keeping your real address private."
        path="/free"
      />
      <main className="min-h-screen bg-white flex items-center justify-center px-4" data-testid="free-inbox-auth">
        <div className="max-w-sm w-full space-y-5 text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-violet-50 flex items-center justify-center">
            <Lock size={24} className="text-violet-600" />
          </div>
          <div>
            <h1 className="font-heading text-xl font-medium text-[#111111]" data-testid="free-auth-title">Deine Inbox ist bereit.</h1>
            <p className="text-sm text-zinc-500 mt-1.5">
              Gib deinen Zugangscode ein, um deine Inbox zu öffnen.
            </p>
          </div>
          <div className="space-y-3 text-left">
            <label htmlFor="free-auth-code-input" className="block text-xs font-medium text-[#555555] tracking-wide">
              Zugangscode
            </label>
            <input
              id="free-auth-code-input"
              type="text"
              value={authCode}
              onChange={(e) => { setAuthCode(e.target.value); setAuthError(""); }}
              placeholder="XXXX-XXXX-XXXX"
              className="w-full h-11 px-4 border border-[#E5E5E5] rounded-lg text-center font-mono text-base tracking-wider focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              data-testid="free-auth-code-input"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && handleAuth()}
            />
            {authError && (
              <p className="text-xs text-red-500" data-testid="free-auth-error">{authError}</p>
            )}
            <Button
              onClick={handleAuth}
              disabled={authLoading || !authCode.trim()}
              className="w-full h-11 bg-violet-600 hover:bg-violet-500 text-white transition-all hover:-translate-y-0.5 hover:shadow-md"
              data-testid="free-auth-submit"
            >
              {authLoading ? "Wird geöffnet..." : "Inbox öffnen"}
            </Button>
          </div>
          <div className="pt-2 flex flex-col gap-1.5">
            <Link to="/?new=1" className="text-xs text-zinc-500 hover:text-[#888888]" data-testid="free-auth-create-new">
              oder neue Inbox erstellen
            </Link>
          </div>
        </div>
      </main>
      </>
    );
  }

  // P0 (May 1 2026) — Catch-all recovery fallback. This guard MUST fire
  // before the main render, which reads `alias.alias_address` directly and
  // would crash to a blank white screen if alias were null (e.g. after iOS
  // Safari discarded our SPA state during a share/save round-trip, or the
  // access code expired server-side). We prefer the previous narrow
  // "Inbox not found" copy when the URL carries a code; otherwise we show a
  // generic recovery screen so the user always has a clear way forward.
  if (!alias) {
    const hasUrlCode = Boolean(urlCode);
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4" data-testid="free-alias-recovery">
        <div className="max-w-sm text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#F5F5F5] flex items-center justify-center">
            <Mail size={28} className="text-zinc-500" />
          </div>
          <h1 className="font-heading text-xl font-medium text-[#111111]" data-testid="free-alias-recovery-title">
            {hasUrlCode ? "Inbox not found" : "Your inbox session ended"}
          </h1>
          <p className="text-sm text-zinc-500" data-testid="free-alias-recovery-message">
            {hasUrlCode
              ? "We couldn't find an inbox for this access code. It may have expired or the code may be incorrect."
              : "We lost your temporary inbox session. Create a new free inbox or open an existing one with your access code."}
          </p>
          <div className="flex flex-col gap-2">
            <Link to="/?new=1" data-testid="free-alias-recovery-home-link">
              <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">Create a new email</Button>
            </Link>
            <Link to="/claim" data-testid="free-alias-recovery-access-link">
              <Button variant="outline" className="w-full">Enter an access code</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const unreadCount = emails.filter((e) => e.status === "unread").length;
  const expInfo = getExpirationInfo();

  return (
    <div className="h-screen flex flex-col bg-white" data-testid="free-inbox-page">
      <DocumentHead
        title="Get a Free Anonymous Email — QuickInbox"
        description="Generate a disposable email address in one click. Perfect for sign-ups, trials, and keeping your real address private."
        path="/free"
      />
      {/* Countdown Banner — tiered urgency */}
      {timeRemaining && !timeRemaining.expired && !timeRemaining.preExpiry && (
        <div className={`px-4 py-1.5 flex items-center justify-center gap-2 border-b ${
          timeRemaining.urgent ? "bg-red-50 border-red-100" : "bg-[#F5F5F5] border-[#E5E5E5]"
        }`} data-testid="inbox-timer-banner">
          <Clock size={12} className={timeRemaining.urgent ? "text-red-600" : "text-zinc-500"} />
          <p className={`text-xs ${timeRemaining.urgent ? "text-red-600 font-medium" : "text-zinc-500"}`} data-testid="inbox-timer-text">
            This address expires in
          </p>
          <span className={`text-xs font-mono tabular-nums ${timeRemaining.urgent ? "text-red-600 font-bold" : "text-[#555555] font-bold"}`} data-testid="inbox-countdown-value">
            {timeRemaining.text}
          </span>
          <button onClick={() => setKeepForeverOpen(true)} className={`text-xs font-medium ml-1 ${timeRemaining.urgent ? "text-red-600 hover:text-red-900" : "text-blue-600 hover:text-blue-600"}`} data-testid="inbox-keep-forever-link">
            Keep forever
          </button>
        </div>
      )}

      {/* Pre-expiry nudge (< 2 min, >= 30 sec) */}
      {timeRemaining && !timeRemaining.expired && timeRemaining.preExpiry && !timeRemaining.lastSeconds && (
        <div className="bg-red-50 border-b border-red-100 px-4 py-2.5" data-testid="pre-expiry-banner">
          <div className="flex items-center justify-center gap-3">
            <div className="flex items-center gap-2">
              <AlertTriangle size={14} className="text-red-500 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-red-600">Your temporary email will expire soon</p>
                <p className="text-[11px] text-red-500 mt-0.5">Keep this address to continue sending and receiving emails</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-2">
              <span className="text-sm font-mono font-black text-red-800 tabular-nums" data-testid="pre-expiry-countdown">{timeRemaining.text}</span>
              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs h-7 px-3" onClick={() => setKeepForeverOpen(true)} data-testid="pre-expiry-keep-btn">
                Keep forever
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Last seconds warning (< 30 sec) */}
      {timeRemaining && !timeRemaining.expired && timeRemaining.lastSeconds && (
        <div className="bg-red-600 border-b border-red-700 px-4 py-3" data-testid="last-seconds-banner">
          <div className="flex items-center justify-center gap-3">
            <div className="flex items-center gap-2">
              <AlertTriangle size={14} className="text-white shrink-0 animate-pulse" />
              <div>
                <p className="text-xs font-bold text-white">Expiring in a few seconds</p>
                <p className="text-[11px] text-red-100 mt-0.5">Keep this address to avoid losing access</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-2">
              <span className="text-sm font-mono font-black text-white tabular-nums" data-testid="last-seconds-countdown">{timeRemaining.text}</span>
              <Button size="sm" className="bg-white hover:bg-red-50 text-red-600 text-xs h-7 px-3 font-semibold" onClick={() => setKeepForeverOpen(true)} data-testid="last-seconds-keep-btn">
                Keep forever
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mailbox Header — persistent, shows email address prominently
          with an INLINE copy icon (no separate blue button). */}
      {alias && (
        <div className="border-b border-[#E5E5E5] bg-white px-4 py-3 shrink-0" data-testid="mailbox-header">
          <div className="max-w-6xl mx-auto">
            <p className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider mb-1.5">Your temporary email</p>
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-base sm:text-lg font-medium text-[#555555] tracking-tight break-all flex-1 min-w-0" data-testid="mailbox-header-address">
                {alias.alias_address}
              </span>
              <button
                type="button"
                onClick={() => handleCopy(alias.alias_address, "hdr-addr")}
                className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md text-zinc-500 hover:text-[#111111] hover:bg-[#F5F5F5] active:bg-[#E5E5E5] transition-colors"
                aria-label={copied === "hdr-addr" ? "Address copied" : "Copy email address"}
                data-testid="mailbox-header-copy"
              >
                {copied === "hdr-addr" ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PROMPT 027 — Conversion banner. HIDDEN in SIMPLIFIED_FLOW so the
          inbox is content-first only. Re-enabled by flipping the flag. */}
      {!SIMPLIFIED_FLOW && alias && !timeRemaining?.expired && aliasChoiceMode === "custom" && (
        !conversionExpanded ? (
          <div
            className="border-b border-emerald-200 bg-emerald-50 px-4 py-2 shrink-0"
            data-testid="trial-conversion-keep-pill"
          >
            <button
              onClick={() => setConversionExpanded(true)}
              className="w-full flex items-center justify-between gap-3 text-left group"
              data-testid="trial-conversion-keep-pill-toggle"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Lock size={13} className="text-emerald-700 shrink-0" />
                <span className="text-[12px] text-emerald-900 font-medium truncate">
                  Like this address? <span className="text-emerald-700 underline-offset-2 group-hover:underline">Keep it permanently</span>
                </span>
              </div>
              <ChevronDown size={14} className="text-emerald-700 shrink-0 group-hover:translate-y-0.5 transition-transform" />
            </button>
          </div>
        ) : (
        <div
          className="border-b border-emerald-200 bg-emerald-50 px-4 py-3 shrink-0"
          data-testid="trial-conversion-keep-banner"
        >
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wider">
                Like this address?
              </p>
              <p className="text-sm text-emerald-900 font-medium mt-0.5 break-all sm:truncate" data-testid="trial-conversion-current-address">
                Keep <span className="font-mono">{alias.alias_address}</span> permanently.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 px-3.5 w-fit"
                onClick={handleSecureCurrent}
                data-testid="trial-conversion-secure-cta"
              >
                <Lock size={13} className="mr-1.5" /> Secure your address
              </Button>
              <button
                onClick={() => setConversionExpanded(false)}
                className="shrink-0 text-emerald-700 hover:text-emerald-900 p-1.5 rounded transition-colors"
                aria-label="Collapse conversion banner"
                data-testid="trial-conversion-keep-collapse"
              >
                <ChevronUp size={14} />
              </button>
            </div>
          </div>

          {/* PROMPT 030 — Option 2: still let the user adjust to a
              different handle if they don't like the one they typed
              originally. Same input + modal flow as the random path. */}
          <div className="max-w-6xl mx-auto mt-3 pt-3 border-t border-emerald-200/60">
            <p className="text-[11px] text-emerald-800 mb-1.5">
              Or pick a different one before checkout
            </p>
            <div className="flex flex-col sm:flex-row sm:items-stretch gap-2">
              <div className="flex-1" data-testid="trial-conversion-keep-input-group">
                <input
                  type="text"
                  placeholder="yourname"
                  value={desiredAlias}
                  onChange={(e) => {
                    let raw = String(e.target.value || "").trim().toLowerCase();
                    if (raw.endsWith("@mail.quickinbox.app")) raw = raw.slice(0, -"@mail.quickinbox.app".length);
                    const at = raw.indexOf("@");
                    if (at >= 0) raw = raw.slice(0, at);
                    const clean = raw.replace(/[^a-zA-Z0-9._-]/g, "");
                    setDesiredAlias(clean);
                    if (desiredAliasError) setDesiredAliasError("");
                  }}
                  onKeyDown={(e) => e.key === "Enter" && handleOpenSwitchModal()}
                  autoComplete="off"
                  inputMode="email"
                  aria-label="Different anonymous address"
                  className={`l-input w-full bg-white ${desiredAliasError ? "border-red-500" : ""}`}
                  data-testid="trial-conversion-keep-desired-input"
                />
                {desiredAlias && (
                  <p className="mt-1.5 text-[11px] text-emerald-800" data-testid="trial-conversion-keep-desired-preview">
                    Will become: <span className="font-mono text-emerald-900">{desiredAlias}@mail.quickinbox.app</span>
                  </p>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                className="shrink-0 border-emerald-300 text-emerald-700 hover:bg-emerald-100 text-xs h-9 px-3.5 sm:w-auto w-full"
                onClick={handleOpenSwitchModal}
                disabled={!desiredAlias.trim()}
                data-testid="trial-conversion-keep-switch-cta"
              >
                Switch instead <ArrowRight size={13} className="ml-1.5" />
              </Button>
            </div>
            {desiredAliasError && (
              <p className="text-[11px] text-red-600 mt-1.5" data-testid="trial-conversion-keep-desired-error">
                {desiredAliasError}
              </p>
            )}
          </div>
        </div>
        )
      )}

      {!SIMPLIFIED_FLOW && alias && !timeRemaining?.expired && aliasChoiceMode === "random" && (
        !conversionExpanded ? (
          <div
            className="border-b border-amber-200 bg-amber-50 px-4 py-2 shrink-0"
            data-testid="trial-conversion-replace-pill"
          >
            <button
              onClick={() => setConversionExpanded(true)}
              className="w-full flex items-center justify-between gap-3 text-left group"
              data-testid="trial-conversion-replace-pill-toggle"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Clock size={13} className="text-amber-700 shrink-0" />
                <span className="text-[12px] text-amber-900 font-medium truncate">
                  Temporary address — <span className="text-amber-700 underline-offset-2 group-hover:underline">choose a permanent one</span>
                </span>
              </div>
              <ChevronDown size={14} className="text-amber-700 shrink-0 group-hover:translate-y-0.5 transition-transform" />
            </button>
          </div>
        ) : (
        <div
          className="border-b border-amber-200 bg-amber-50 px-4 py-4 shrink-0 relative"
          data-testid="trial-conversion-replace-banner"
        >
          <button
            onClick={() => setConversionExpanded(false)}
            className="absolute top-2 right-2 text-amber-700 hover:text-amber-900 p-1.5 rounded transition-colors"
            aria-label="Collapse conversion banner"
            data-testid="trial-conversion-replace-collapse"
          >
            <ChevronUp size={14} />
          </button>
          <div className="max-w-6xl mx-auto space-y-3">
            {/* PROMPT 033 — structured "choose your own" block.
                Full-email-input is gone; the user types ONLY the local
                part, sees a split-view preview (local | @domain) and
                taps a single "Secure this address →" CTA that flows
                directly to /pricing?keep_alias=<local>. No modal in
                the random path — the modal is reserved for the custom
                "Switch instead" path where comparing matters. */}
            <div data-testid="trial-conversion-temp-address-line">
              <p className="text-[11px] font-semibold text-amber-700 uppercase tracking-wider">
                Your temporary address
              </p>
              <p className="font-mono text-xs text-amber-900/80 break-all mt-0.5" data-testid="trial-conversion-temp-address">
                {alias.alias_address}
              </p>
            </div>

            <div data-testid="trial-conversion-choose-block">
              <p className="text-[11px] font-semibold text-[#111111] uppercase tracking-wider">
                Choose your own address
              </p>
              <input
                type="text"
                placeholder="yourname"
                value={desiredAlias}
                onChange={(e) => {
                  // PROMPT 033: ONLY local-part allowed. Strip the
                  // domain or any '@…' the user might paste, then
                  // restrict to the alias charset, then lowercase.
                  let raw = String(e.target.value || "").trim().toLowerCase();
                  if (raw.endsWith("@mail.quickinbox.app")) raw = raw.slice(0, -"@mail.quickinbox.app".length);
                  const at = raw.indexOf("@");
                  if (at >= 0) raw = raw.slice(0, at);
                  const clean = raw.replace(/[^a-zA-Z0-9._-]/g, "");
                  setDesiredAlias(clean);
                  if (desiredAliasError) setDesiredAliasError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const err = validateDesiredAlias(desiredAlias);
                    if (err) { setDesiredAliasError(err); return; }
                    goToPricingWithAlias(desiredAlias);
                  }
                }}
                autoComplete="off"
                inputMode="email"
                aria-label="Your desired anonymous address"
                className={`l-input w-full mt-2 bg-white ${desiredAliasError ? "border-red-500" : ""}`}
                data-testid="trial-conversion-desired-input"
              />

              {/* Split-view live preview — local | @domain (dropdown placeholder).
                  Always visible so users see the structure even with an
                  empty input; switches from placeholder grey to solid
                  text once they type. */}
              <div
                className="mt-2 flex items-stretch gap-2 text-[12px]"
                data-testid="trial-conversion-preview-row"
                aria-live="polite"
              >
                <div
                  className="flex-1 min-w-0 px-3 py-2 rounded-md border border-[#E5E5E5] bg-white font-mono truncate"
                  data-testid="trial-conversion-preview-local"
                  title={desiredAlias || "yourname"}
                >
                  {desiredAlias
                    ? <span className="text-[#111111]">{desiredAlias}</span>
                    : <span className="text-zinc-400">yourname</span>}
                </div>
                <button
                  type="button"
                  disabled
                  aria-label="Domain — more domains coming soon"
                  className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#E5E5E5] bg-white font-mono text-zinc-500 cursor-not-allowed disabled:opacity-100"
                  data-testid="trial-conversion-preview-domain"
                  title="More domains coming soon"
                >
                  @mail.quickinbox.app
                  <ChevronDown size={12} className="text-zinc-400" />
                </button>
              </div>

              {desiredAliasError && (
                <p className="mt-1.5 text-[11px] text-red-600" data-testid="trial-conversion-desired-error">
                  {desiredAliasError}
                </p>
              )}

              <Button
                size="sm"
                className="mt-3 bg-[#E11D2E] hover:bg-[#C8102B] text-white text-xs h-9 px-4 w-full sm:w-auto"
                onClick={() => {
                  const err = validateDesiredAlias(desiredAlias);
                  if (err) { setDesiredAliasError(err); return; }
                  goToPricingWithAlias(desiredAlias);
                }}
                disabled={!desiredAlias.trim()}
                data-testid="trial-conversion-secure-cta"
              >
                Secure this address <ArrowRight size={13} className="ml-1.5" />
              </Button>
            </div>
          </div>
        </div>
        )
      )}

      {/* PROMPT 027 — Switch & keep modal: shows temporary vs custom, then
          punts to /pricing?keep_alias=<custom>. The actual swap-and-keep
          happens after payment (Phase B will wire the webhook). */}
      <Dialog open={switchModalOpen} onOpenChange={setSwitchModalOpen}>
        <DialogContent className="sm:max-w-[440px]" data-testid="trial-switch-modal" aria-describedby="trial-switch-modal-desc">
          <DialogHeader>
            <DialogTitle>Replace your temporary email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2" id="trial-switch-modal-desc">
            <div className="rounded-md border border-[#E5E5E5] bg-[#FAFAFA] p-3">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Temporary</p>
              <p className="font-mono text-sm text-zinc-700 break-all mt-1" data-testid="trial-switch-modal-old">
                {alias?.alias_address}
              </p>
            </div>
            <div className="flex justify-center">
              <ArrowRight size={18} className="text-zinc-400 rotate-90" />
            </div>
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
              <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">New — your own</p>
              <p className="font-mono text-sm text-emerald-900 font-medium break-all mt-1" data-testid="trial-switch-modal-new">
                {desiredAlias}@mail.quickinbox.app
              </p>
            </div>
            <p className="text-xs text-zinc-600">
              Replace your temporary email with your personal anonymous address. Permanent only after checkout.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSwitchModalOpen(false)}
              data-testid="trial-switch-modal-cancel"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={handleConfirmSwitch}
              data-testid="trial-switch-modal-confirm"
            >
              Switch &amp; keep this address <ArrowRight size={13} className="ml-1.5" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header — CORE_PROMPT_003 sticky on desktop.
          The free-inbox is currently `h-screen flex flex-col`, so the
          page itself doesn't scroll today — but adding the sticky
          modifier makes the toolbar resilient against any future
          layout where document-flow scrolling kicks in (e.g., when an
          additional banner pushes content past viewport). On mobile /
          tablet (<1280 px) we leave the natural in-flow positioning so
          we don't fight with the new bottom action row. The `pt-safe`
          reserves space for the iPad / notched-device top inset using
          the CORE_001 utility, so the back arrow never sits under the
          notch even when the header is stuck to the top. */}
      <header className="border-b border-[#E5E5E5] shrink-0 xl:sticky xl:top-0 xl:z-20 xl:bg-white/95 xl:backdrop-blur pt-safe">
        <div className="max-w-6xl mx-auto px-4 h-12 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            {selectedEmail ? (
              /* Detail view → return to list (preserves alias + token + cached
                 emails). Never navigate to "/" — that would regenerate the
                 trial address and lose the user's session. */
              <button
                type="button"
                onClick={() => setSelectedEmail(null)}
                className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-[#111111] transition-colors text-sm font-medium"
                data-testid="free-inbox-home-link"
              >
                <ArrowLeft size={16} />
                <span>Inbox</span>
              </button>
            ) : (
              /* List view — already at the inbox root for this alias.
                 The label is a non-interactive marker, no nav. */
              <span
                className="inline-flex items-center gap-1.5 text-zinc-500 text-sm font-medium select-none"
                data-testid="free-inbox-home-link"
                aria-current="page"
              >
                <Mail size={15} />
                <span>Inbox</span>
              </span>
            )}
          </div>
          <div className="hidden xl:flex items-center gap-2">
            {/* Invite friends */}
            {alias && (
              <SharePopover inboxToken={inboxTokenRef.current} />
            )}
            {/* Access code — only visible if user has it from creation.
                PROMPT 014: replaced inline reveal with a focused modal so
                the toolbar stays clean and the code/countdown gets proper
                explanation. */}
            {secretAccessCode && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAccessCode(true)}
                className="text-zinc-500 hover:text-[#888888] text-xs"
                data-testid="show-access-code-button"
              >
                <Key size={13} className="mr-1" /> Show access code
              </Button>
            )}
            {SIMPLIFIED_FLOW ? (
              <Link to={upgradeUrl} data-testid="free-inbox-upgrade-button">
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs">
                  Use your own anonymous address
                </Button>
              </Link>
            ) : (
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs" onClick={() => setKeepForeverOpen(true)} data-testid="free-inbox-upgrade-button">
                Keep forever
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Content — shows expired state or normal inbox */}
      {timeRemaining?.expired ? (
        <div className="flex-1 flex items-center justify-center px-4" data-testid="expired-full-state">
          <div className="max-w-sm text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-[#F5F5F5] flex items-center justify-center">
              <Clock size={28} className="text-[#555555]" />
            </div>
            <h2 className="text-xl font-semibold text-[#111111]">Your session has expired</h2>
            <p className="text-sm text-zinc-500">All emails and attachments have been deleted.</p>
            <div className="flex flex-col gap-2 pt-2">
              {/* PROMPT 027 — primary expired CTA is now a plan choice,
                  not a fresh trial. Trial path kept as a quiet fallback. */}
              <Link to="/pricing" data-testid="expired-choose-plan">
                <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  Choose a plan to continue
                </Button>
              </Link>
              <Link to="/?new=1" data-testid="expired-create-new" className="block">
                <Button variant="outline" className="w-full">
                  Or start another 30-min trial
                </Button>
              </Link>
            </div>
          </div>
        </div>
      ) : (
      <div className="flex-1 flex overflow-hidden">
        {/* Email List */}
        <div className={`${selectedEmail ? "hidden md:flex" : "flex"} w-full md:w-[360px] lg:w-[420px] md:border-r border-[#E5E5E5] flex-col`}>

          {/* List Header with Tabs */}
          <div className="px-4 py-2 border-b border-[#E5E5E5] space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleFolderSwitch("inbox")}
                  className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                    activeFolder === "inbox"
                      ? "bg-zinc-900 text-white"
                      : "text-zinc-500 hover:text-[#555555] hover:bg-[#F5F5F5]"
                  }`}
                  data-testid="folder-tab-inbox"
                >
                  Inbox
                  {activeFolder === "inbox" && unreadCount > 0 && (
                    <span className="ml-1.5 text-[10px] font-mono bg-white/20 px-1 rounded">{unreadCount}</span>
                  )}
                </button>
                <button
                  onClick={() => handleFolderSwitch("sent")}
                  className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                    activeFolder === "sent"
                      ? "bg-zinc-900 text-white"
                      : "text-zinc-500 hover:text-[#555555] hover:bg-[#F5F5F5]"
                  }`}
                  data-testid="folder-tab-sent"
                >
                  Sent
                </button>
                <Badge variant="outline" className="text-[9px] font-mono text-zinc-500 border-[#E5E5E5] ml-1">FREE</Badge>
              </div>
              <div className="flex items-center gap-1.5">
                {/* Refresh — ALWAYS visible (P0 fix Apr 30). Single touch
                    target, 44 px tall on mobile. Dropdown still has a
                    duplicate entry as a fallback. */}
                <button
                  type="button"
                  onClick={handleManualRefresh}
                  disabled={refreshing}
                  className="inline-flex items-center justify-center w-11 h-11 rounded-md text-zinc-500 hover:text-[#111111] hover:bg-[#F5F5F5] active:bg-[#E5E5E5] disabled:opacity-60 transition-colors"
                  aria-label="Refresh inbox"
                  data-testid="free-inbox-refresh-button"
                >
                  <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
                </button>
                {/* Compose — promoted to a primary brand button so the
                    affordance is unmistakable and meets the 44 px touch
                    target. */}
                <Button
                  size="sm"
                  onClick={() => setComposeOpen(true)}
                  className="h-11 px-4 bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium"
                  data-testid="free-inbox-compose-button"
                >
                  <PenLine size={15} className="mr-1.5" /> Compose
                </Button>
                {/* Mobile-only secondary actions menu (PROMPT 016 + CORE_PROMPT_002).
                    Consolidates Show access code / Refresh — Invite friend
                    and Keep forever lifted out into a visible bottom action
                    row (see further below: data-testid="mobile-action-row").
                    Desktop layout (≥1280 px) unchanged. */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="xl:hidden h-9 w-9 p-0 text-zinc-500 hover:text-[#111111]"
                      aria-label="More actions"
                      data-testid="free-inbox-more-menu"
                    >
                      <MoreVertical size={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56" data-testid="free-inbox-more-menu-content">
                    {secretAccessCode && (
                      <DropdownMenuItem
                        onClick={() => setShowAccessCode(true)}
                        className="cursor-pointer"
                        data-testid="mobile-menu-show-access-code"
                      >
                        <Key size={14} className="mr-2 text-zinc-500" />
                        Show access code
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={handleManualRefresh}
                      disabled={refreshing}
                      className="cursor-pointer"
                      data-testid="mobile-menu-refresh"
                    >
                      <RefreshCw size={14} className={`mr-2 text-zinc-500 ${refreshing ? "animate-spin" : ""}`} />
                      Refresh
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            {!SIMPLIFIED_FLOW && activeFolder === "inbox" && (
              <div className="hidden md:flex items-center gap-1">
                <button
                  onClick={handleSendTestEmail}
                  disabled={sendingTest}
                  className="text-[11px] text-zinc-500 hover:text-[#888888] transition-colors px-1.5 py-0.5"
                  data-testid="free-inbox-send-test"
                >
                  {sendingTest ? "Sending..." : "Send test"}
                </button>
                <span className="text-[#555555]">|</span>
                <button
                  onClick={handleSeedDemo}
                  disabled={seeding}
                  className="text-[11px] text-zinc-500 hover:text-[#888888] transition-colors px-1.5 py-0.5"
                  data-testid="free-inbox-seed-demo"
                >
                  {seeding ? "..." : "View examples"}
                </button>
              </div>
            )}
            {/* Usage display (desktop only — mobile shows it below the email list to keep the inbox content-first) */}
            {!SIMPLIFIED_FLOW && (
            <div className="hidden md:flex items-center gap-3 px-4 py-1.5 border-b border-zinc-50 text-[10px] font-mono text-zinc-500" data-testid="usage-display">
              <span data-testid="usage-sent">Sent: {sendStatus.total != null ? `${sendStatus.total - (sendStatus.remaining ?? 0)} / ${sendStatus.total}` : "..."}</span>
              <span className="text-[#555555]">|</span>
              <span data-testid="usage-received">Received: {activeFolder === "inbox" ? emails.length : prevEmailIdsRef.current.size} (unlimited)</span>
            </div>
            )}
          </div>

          {/* Activity Stats + Feed — DESKTOP ONLY. On mobile this block is rendered
              BELOW the email list (see further down) so received messages stay
              the first scroll content. HIDDEN entirely in SIMPLIFIED_FLOW. */}
          {!SIMPLIFIED_FLOW && (
          <div className="hidden md:block px-3 py-2 border-b border-[#E5E5E5] shrink-0 space-y-2">
            <AliasActivity claimCode={claimCode || urlCode} />
            <NotificationSettings claimCode={claimCode || urlCode} />
          </div>
          )}

          <ScrollArea className="flex-1">
            {emails.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-14 px-6 text-center" data-testid="free-inbox-empty">
                <div className="w-12 h-12 rounded-xl bg-[#F5F5F5] flex items-center justify-center mb-3">
                  {activeFolder === "sent"
                    ? <Send size={22} className="text-[#555555]" strokeWidth={1.5} />
                    : <Mail size={22} className="text-[#555555]" strokeWidth={1.5} />
                  }
                </div>
                <h3 className="text-sm font-medium text-[#555555] mb-1">
                  {activeFolder === "sent" ? "No sent emails yet" : "No emails yet"}
                </h3>
                <p className="text-xs text-zinc-500 mb-5 max-w-[250px]" data-testid="free-inbox-waiting">
                  {activeFolder === "sent"
                    ? "Compose an email to see it here."
                    : "Waiting for incoming emails..."
                  }
                </p>
                {activeFolder === "sent" ? (
                  <Button
                    size="sm"
                    className="bg-zinc-900 hover:bg-zinc-800 text-white"
                    onClick={() => setComposeOpen(true)}
                    data-testid="free-sent-compose-button"
                  >
                    <PenLine size={14} className="mr-1.5" /> Compose email
                  </Button>
                ) : (
                  <>
                    {/* Use-case guidance — HIDDEN in SIMPLIFIED_FLOW */}
                    {!SIMPLIFIED_FLOW && (
                    <div className="w-full max-w-[260px] space-y-2 mb-4" data-testid="empty-inbox-guide">
                      <p className="text-[10px] uppercase tracking-widest text-[#555555]">Try this</p>
                      {[
                        { text: "Use this address when signing up for a new service", icon: Shield },
                        { text: "Forward emails from here to your real inbox", icon: ArrowRight },
                        { text: "Set up rules to auto-filter what comes in", icon: Mail },
                      ].map(({ text, icon: Icon }, i) => (
                        <div key={i} className="flex items-start gap-2 text-left">
                          <Icon size={12} className="text-[#555555] shrink-0 mt-0.5" />
                          <p className="text-[11px] text-zinc-500 leading-relaxed">{text}</p>
                        </div>
                      ))}
                    </div>
                    )}

                    {/* Single conversion CTA lives in the sticky mobile-
                        action-row (always visible at the bottom). The
                        previous duplicate empty-state CTA has been
                        removed to prevent two identical "Use your own
                        anonymous address" buttons stacking on the same
                        screen. */}
                  </>
                )}
              </div>
            ) : (
              emails.map((email) => (
                <FreeEmailItem
                  key={email.email_id}
                  email={email}
                  isSelected={selectedEmail?.email_id === email.email_id}
                  isSent={activeFolder === "sent"}
                  onClick={() => handleSelectEmail(email)}
                  onArchive={() => handleAction(email.email_id, "archive")}
                  onDelete={() => handleAction(email.email_id, "delete")}
                />
              ))
            )}

            {/* Soft upgrade card — only after user has engaged with emails. HIDDEN in SIMPLIFIED_FLOW. */}
            {!SIMPLIFIED_FLOW && hasReceivedEmail && hasEngaged && (
              <div className="mx-4 my-3 p-3.5 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5]" data-testid="free-inbox-upgrade-card">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
                    <Crown size={14} className="text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#555555]">Don't lose this address</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">
                      Keep your address, emails, and attachments permanently.
                    </p>
                    <Link to={upgradeUrl}>
                      <button className="mt-2 text-xs font-medium text-blue-600 hover:text-blue-600 transition-colors" data-testid="free-upgrade-card-cta">
                        Upgrade <ArrowRight size={12} className="inline ml-0.5" />
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* ── Mobile-only secondary panels — rendered AFTER the email list
                so the inbox stays content-first on small viewports. Desktop
                keeps the legacy fixed-panel layout above and below the
                ScrollArea (gated with hidden md:block). ── */}
            {emails.length > 0 && activeFolder === "inbox" && !SIMPLIFIED_FLOW && (
              <div className="md:hidden border-t border-[#E5E5E5]" data-testid="mobile-secondary-panels">
                {/* Tools row + counters */}
                <div className="px-4 pt-3 pb-2 space-y-2">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={handleSendTestEmail}
                      disabled={sendingTest}
                      className="text-[11px] text-zinc-500 hover:text-[#888888] transition-colors px-1.5 py-0.5"
                      data-testid="free-inbox-send-test-mobile"
                    >
                      {sendingTest ? "Sending..." : "Send test"}
                    </button>
                    <span className="text-[#555555]">|</span>
                    <button
                      onClick={handleSeedDemo}
                      disabled={seeding}
                      className="text-[11px] text-zinc-500 hover:text-[#888888] transition-colors px-1.5 py-0.5"
                      data-testid="free-inbox-seed-demo-mobile"
                    >
                      {seeding ? "..." : "View examples"}
                    </button>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] font-mono text-zinc-500" data-testid="usage-display-mobile">
                    <span>Sent: {sendStatus.total != null ? `${sendStatus.total - (sendStatus.remaining ?? 0)} / ${sendStatus.total}` : "..."}</span>
                    <span className="text-[#555555]">|</span>
                    <span>Received: {emails.length} (unlimited)</span>
                  </div>
                </div>
                {/* Activity + Notifications */}
                <div className="px-3 py-2 border-t border-[#E5E5E5] space-y-2">
                  <AliasActivity claimCode={claimCode || urlCode} />
                  <NotificationSettings claimCode={claimCode || urlCode} />
                </div>
                {/* Rules */}
                <div className="px-3 py-2.5 border-t border-[#E5E5E5]">
                  <AliasRules claimCode={claimCode || urlCode} aliasAddress={alias?.alias_address} />
                </div>
                {/* Storage */}
                {storageInfo && (
                  <div className="px-4 py-2.5 border-t border-[#E5E5E5]" data-testid="storage-bar-mobile">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Storage</span>
                      <span className="text-[10px] text-zinc-500 tabular-nums">
                        {storageInfo.used_mb} / {storageInfo.max_mb} MB
                      </span>
                    </div>
                    <div className="h-1.5 w-full bg-[#F5F5F5] rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          storageInfo.percentage >= 90
                            ? "bg-red-500"
                            : storageInfo.percentage >= 70
                              ? "bg-amber-500"
                              : "bg-blue-500"
                        }`}
                        style={{ width: `${Math.min(storageInfo.percentage, 100)}%` }}
                      />
                    </div>
                    {storageInfo.storage_full && (
                      <p className="text-[10px] text-red-500 mt-1 font-medium">
                        Storage full — new attachments will not be saved
                      </p>
                    )}
                    {!storageInfo.storage_full && storageInfo.percentage >= 80 && (
                      <p className="text-[10px] text-amber-500 mt-1">
                        Storage almost full — {storageInfo.remaining_mb} MB remaining
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </ScrollArea>

          {/* Email Rules — DESKTOP ONLY (mobile renders it inside the ScrollArea above). HIDDEN in SIMPLIFIED_FLOW. */}
          {!SIMPLIFIED_FLOW && (
          <div className="hidden md:block px-3 py-2.5 border-t border-[#E5E5E5] shrink-0">
            <AliasRules claimCode={claimCode || urlCode} aliasAddress={alias?.alias_address} />
          </div>
          )}

          {/* Storage Usage Bar — DESKTOP ONLY (mobile renders it inside the ScrollArea above). HIDDEN in SIMPLIFIED_FLOW. */}
          {!SIMPLIFIED_FLOW && storageInfo && (
            <div className="hidden md:block px-4 py-2.5 border-t border-[#E5E5E5] shrink-0" data-testid="storage-bar">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Storage</span>
                <span className="text-[10px] text-zinc-500 tabular-nums">
                  {storageInfo.used_mb} / {storageInfo.max_mb} MB
                </span>
              </div>
              <div className="h-1.5 w-full bg-[#F5F5F5] rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    storageInfo.percentage >= 90
                      ? "bg-red-500"
                      : storageInfo.percentage >= 70
                        ? "bg-amber-500"
                        : "bg-blue-500"
                  }`}
                  style={{ width: `${Math.min(storageInfo.percentage, 100)}%` }}
                  data-testid="storage-bar-fill"
                />
              </div>
              {storageInfo.storage_full && (
                <p className="text-[10px] text-red-500 mt-1 font-medium" data-testid="storage-full-warning">
                  Storage full — new attachments will not be saved
                </p>
              )}
              {!storageInfo.storage_full && storageInfo.percentage >= 80 && (
                <p className="text-[10px] text-amber-500 mt-1" data-testid="storage-warning">
                  Storage almost full — {storageInfo.remaining_mb} MB remaining
                </p>
              )}
            </div>
          )}
        </div>

        {/* Desktop Detail */}
        <div className="hidden md:flex flex-1 bg-[#F5F5F5]">
          {selectedEmail ? (
            <FreeEmailDetail email={selectedEmail} onAction={handleAction} upgradeUrl={upgradeUrl} isSent={activeFolder === "sent"} claimCode={claimCode || urlCode} />
          ) : (
            <div className="flex-1 flex items-center justify-center px-8">
              <div className="text-center space-y-2 max-w-xs">
                <MailOpen size={28} className="text-[#555555] mx-auto" />
                <p className="text-sm text-zinc-500">
                  {activeFolder === "sent" ? "Select a sent email" : "Select an email to read"}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Mobile Detail Overlay */}
        <AnimatePresence>
          {selectedEmail && (
            <motion.div
              className="md:hidden fixed inset-0 bg-white z-40"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
            >
              <div className="h-full flex flex-col">
                <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E5E5E5]">
                  <Button variant="ghost" size="sm" onClick={() => setSelectedEmail(null)} data-testid="free-mobile-back" className="shrink-0 h-9 px-2">
                    <ArrowLeft size={18} />
                  </Button>
                  <span className="text-sm font-medium text-[#555555] truncate flex-1 min-w-0">
                    {activeFolder === "sent" ? `To: ${selectedEmail.to_email}` : selectedEmail.sender_name}
                  </span>
                  {/* Mobile action bar — primary actions next to back. Reply
                      and Forward are visually represented but call onAction
                      (which surfaces a friendly toast since send-as is a
                      Pro-only action on the trial flow). Archive + Delete
                      are wired to the existing /free/action backend. */}
                  <div className="flex items-center gap-0.5 shrink-0" data-testid="email-detail-actionbar">
                    <button
                      type="button"
                      onClick={() => handleAction(selectedEmail.email_id, "reply")}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-md text-zinc-500 hover:text-[#111111] hover:bg-[#F5F5F5] active:bg-[#E5E5E5] transition-colors"
                      aria-label="Reply"
                      data-testid="email-detail-reply"
                    >
                      <Reply size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAction(selectedEmail.email_id, "forward")}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-md text-zinc-500 hover:text-[#111111] hover:bg-[#F5F5F5] active:bg-[#E5E5E5] transition-colors"
                      aria-label="Forward"
                      data-testid="email-detail-forward"
                    >
                      <Forward size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAction(selectedEmail.email_id, "archive")}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-md text-zinc-500 hover:text-[#111111] hover:bg-[#F5F5F5] active:bg-[#E5E5E5] transition-colors"
                      aria-label="Archive"
                      data-testid="email-detail-archive"
                    >
                      <Archive size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAction(selectedEmail.email_id, "delete")}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-md text-red-500 hover:text-red-600 hover:bg-red-50 active:bg-red-100 transition-colors"
                      aria-label="Delete"
                      data-testid="email-detail-delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-auto">
                  <FreeEmailDetail email={selectedEmail} onAction={handleAction} upgradeUrl={upgradeUrl} isSent={activeFolder === "sent"} claimCode={claimCode || urlCode} />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      )}

      {/* Access Code Modal — PROMPT 014 */}
      <AccessCodeModal
        open={showAccessCode}
        onOpenChange={setShowAccessCode}
        accessCode={secretAccessCode}
        timeRemaining={timeRemaining}
      />

      {/* CORE_PROMPT_002 — Mobile Action Row.
          Sits as a sticky bottom bar on tablet/phone widths only
          (<1280 px); replaces the previous "...→ Invite/Keep forever"
          dropdown affordance. Important actions are now ALWAYS one tap
          away, never hidden behind a kebab menu.
            • z-30 keeps it above the inbox list but below dialogs.
            • `pb-kb` (CORE_001 utility) ensures the bar clears the iOS
              home indicator AND the soft keyboard with no device code.
            • Hidden when the trial has expired (the expired full-state
              already exposes its own CTAs) and on desktop where the
              header inline-actions are already visible.
            • Hidden when no alias has loaded yet so we never flash an
              empty bar before the inbox is ready.
            • Static spacer below the email list adds padding so the
              last email isn't covered by this bar. */}
      {alias && !timeRemaining?.expired && (
        <div
          aria-hidden="true"
          data-testid="mobile-action-row-spacer"
          className="xl:hidden h-[88px] shrink-0"
        />
      )}
      {alias && !timeRemaining?.expired && (
        <div
          className="xl:hidden fixed inset-x-0 bottom-0 z-30 bg-white border-t border-[#E5E5E5] pb-kb"
          data-testid="mobile-action-row"
          role="toolbar"
          aria-label="Inbox actions"
        >
          <div
            className="max-w-6xl mx-auto px-3 py-2 flex items-center gap-2 overflow-x-auto"
            style={{ minHeight: "48px" }}
          >
            {SIMPLIFIED_FLOW ? (
              /* SIMPLIFIED_FLOW — single full-width upgrade CTA. */
              <Link to={upgradeUrl} className="flex-1" data-testid="mobile-action-use-own-address">
                <Button className="w-full h-11 bg-[#E11D2E] hover:bg-[#C8102B] text-white text-sm font-medium">
                  <Crown size={15} className="mr-1.5" />
                  Use your own anonymous address
                </Button>
              </Link>
            ) : (
            <>
            {/* Secondary — Invite friend (outline). SharePopover renders
                its own trigger; we style it as a 44 px-tall outline pill
                so it inherits the platform-friendly touch target. */}
            <div className="shrink-0">
              <SharePopover
                inboxToken={inboxTokenRef.current}
                triggerClassName="inline-flex items-center justify-center gap-1.5 h-11 px-4 rounded-md border border-[#E5E5E5] bg-white text-sm font-medium text-[#111111] hover:bg-[#FAFAFA] active:bg-[#F5F5F5]"
                triggerContent={
                  <>
                    <UserPlus size={15} className="text-zinc-500" />
                    <span>Invite friend</span>
                  </>
                }
              />
            </div>
            {/* Primary — Keep forever (red brand). flex-1 so it always
                fills the remaining row width on phones. */}
            <Button
              onClick={() => setKeepForeverOpen(true)}
              className="flex-1 h-11 bg-[#E11D2E] hover:bg-[#C8102B] text-white text-sm font-medium"
              data-testid="mobile-action-keep-forever"
            >
              <Crown size={15} className="mr-1.5" />
              Keep forever
            </Button>
            </>
            )}
          </div>
        </div>
      )}


      <Dialog open={keepForeverOpen} onOpenChange={setKeepForeverOpen}>
        <DialogContent className="sm:max-w-[380px] p-6" data-testid="keep-forever-modal">
          <DialogHeader className="space-y-1.5">
            <DialogTitle className="text-lg font-semibold text-[#111111]">Keep this address</DialogTitle>
            <p className="text-sm text-zinc-500">Turn this temporary address into a permanent inbox.</p>
          </DialogHeader>
          <ul className="space-y-3 py-4">
            <li className="flex items-center gap-3 text-sm text-[#555555]">
              <Check size={16} className="text-green-500 shrink-0" />
              No expiration — keep your address forever
            </li>
            <li className="flex items-center gap-3 text-sm text-[#555555]">
              <Check size={16} className="text-green-500 shrink-0" />
              Send and receive emails anytime
            </li>
            <li className="flex items-center gap-3 text-sm text-[#555555]">
              <Check size={16} className="text-green-500 shrink-0" />
              Full access to all features
            </li>
          </ul>
          <div className="flex flex-col gap-2 pt-1">
            <Link to={upgradeUrl} className="w-full">
              <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white h-10" data-testid="keep-forever-upgrade-btn">
                Upgrade now
              </Button>
            </Link>
            <Button variant="ghost" className="w-full text-zinc-500 hover:text-[#888888] text-sm h-9" onClick={() => setKeepForeverOpen(false)} data-testid="keep-forever-later-btn">
              Maybe later
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Compose Email Dialog */}
      <ComposeEmail
        open={composeOpen}
        onOpenChange={setComposeOpen}
        aliasId={alias?.alias_id}
        aliasAddress={alias?.alias_address}
        sendsRemaining={sendStatus.remaining}
        sendsTotal={sendStatus.total}
        onSent={(result) => {
          fetchSendStatus();
          fetchStorageInfo();
          // Refresh sent folder if currently viewing it, or switch to it
          const code = claimCode || urlCode;
          if (code) {
            if (activeFolder === "sent") {
              fetchInbox(code, false, "sent");
            }
          }
        }}
      />
    </div>
  );
}

function FreeEmailItem({ email, isSelected, isSent, onClick, onArchive, onDelete }) {
  const isUnread = !isSent && email.status === "unread";
  const timeStr = email.created_at ? formatDistanceToNow(parseUTC(email.created_at), { addSuffix: true }) : "";
  const displayName = isSent ? `To: ${email.to_email || "—"}` : email.sender_name;

  return (
    <div
      className={`border-b border-zinc-50 cursor-pointer email-item-hover ${isSelected ? "bg-blue-50/40" : ""}`}
      onClick={onClick}
      data-testid={`free-email-item-${email.email_id}`}
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="pt-2 w-3 shrink-0 flex justify-center">
          {isSent ? <Send size={10} className="text-[#555555]" /> : isUnread ? <div className="unread-dot" /> : null}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className={`text-sm truncate ${isUnread ? "font-semibold text-[#111111]" : "font-medium text-[#555555]"}`}>{displayName}</span>
            <span className="text-[11px] text-zinc-500 shrink-0 font-mono">{timeStr}</span>
          </div>
          <p className={`text-sm truncate mt-0.5 ${isUnread ? "text-[#555555]" : "text-[#888888]"}`}>{email.subject}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <p className="text-xs text-zinc-500 truncate flex-1">{email.body_preview}</p>
            {email.has_attachments && (
              <span className="text-[10px] text-zinc-500 flex items-center gap-0.5 shrink-0" data-testid={`email-att-badge-${email.email_id}`}>
                <Paperclip size={10} /> {(email.attachments || []).length}
              </span>
            )}
            {(email.rejected_attachments || []).length > 0 && (
              <span className="text-[10px] text-amber-500 flex items-center gap-0.5 shrink-0" data-testid={`email-rejected-badge-${email.email_id}`}>
                <AlertTriangle size={10} />
              </span>
            )}
          </div>
        </div>
        <div className="hidden md:flex quick-actions-container items-center gap-1 pt-1 shrink-0" onClick={(e) => e.stopPropagation()}>
          <button onClick={onArchive} className="p-1.5 rounded hover:bg-amber-50 text-zinc-500 hover:text-amber-600" title="Archive" data-testid={`free-archive-${email.email_id}`}>
            <Archive size={14} />
          </button>
          <button onClick={onDelete} className="p-1.5 rounded hover:bg-red-50 text-zinc-500 hover:text-red-500" title="Delete" data-testid={`free-delete-${email.email_id}`}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function FreeEmailDetail({ email, onAction, upgradeUrl, isSent, claimCode }) {
  const date = email.created_at ? format(parseUTC(email.created_at), "MMM d, yyyy 'at' h:mm a") : "";
  const API = typeof window !== "undefined" ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || "");
  const [previewAtt, setPreviewAtt] = useState(null);

  const storedAtts = email.attachments || [];
  const rejectedAtts = email.rejected_attachments || [];
  const receivedCount = email.attachments_received_count || 0;

  const formatSize = (bytes) => {
    if (!bytes) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  /**
   * Build a same-origin URL to the attachment endpoint.
   *
   * Auth: relies on the `free_inbox_token` HttpOnly cookie that's set
   * when the inbox is unlocked. Cookies are auto-sent by the browser
   * on direct GETs, including <a href> anchor clicks — no JS plumbing
   * needed. iOS Safari handles native anchors (with Content-Disposition
   * from the backend) correctly, while the previous `fetch → blob →
   * a.click()` pattern silently failed on iPhone/iPad.
   */
  const buildAttachmentUrl = (att) =>
    `${API}/api/free/attachment/${claimCode}/${email.email_id}/${att.attachment_id}`;

  // Determine display info based on sent/inbox context
  const avatarChar = isSent
    ? (email.to_email?.charAt(0)?.toUpperCase() || "?")
    : (email.sender_name?.charAt(0)?.toUpperCase() || "?");
  const displayName = isSent ? `To: ${email.to_email || "—"}` : email.sender_name;
  const displayEmail = isSent ? email.to_email : email.sender_email;

  const reasonLabel = (reason) => strings.attachments.reasons[reason] || "Not saved";

  return (
    <div className="flex-1 flex flex-col" data-testid="free-email-detail">
      <div className="flex items-center gap-1 px-4 sm:px-6 py-3 border-b border-[#E5E5E5] bg-white">
        <Button variant="ghost" size="sm" onClick={() => onAction(email.email_id, "archive")} className="text-zinc-500 hover:text-amber-600" data-testid="free-detail-archive">
          <Archive size={15} className="mr-1.5" /> Archive
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onAction(email.email_id, "delete")} className="text-zinc-500 hover:text-red-500" data-testid="free-detail-delete">
          <Trash2 size={15} className="mr-1.5" /> Delete
        </Button>
        {isSent && (
          <Badge variant="secondary" className="ml-2 text-[10px] font-mono bg-emerald-50 text-emerald-600">
            <Send size={9} className="mr-1" /> Sent
          </Badge>
        )}
      </div>
      <div className="flex-1 overflow-auto px-4 sm:px-6 py-5 space-y-4">
        {/* 1. Sender / subject header */}
        <h2 className="font-heading text-xl tracking-tight font-medium text-[#111111]">{email.subject}</h2>
        <Separator />
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium shrink-0 ${
            isSent ? "bg-emerald-100 text-emerald-600" : "bg-zinc-200 text-[#888888]"
          }`}>
            {avatarChar}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-sm font-semibold text-[#111111]">{displayName}</p>
              <span className="text-[11px] text-zinc-500 flex items-center gap-1"><Clock size={11} /> {date}</span>
            </div>
            <p className="text-xs text-zinc-500">{displayEmail}</p>
          </div>
        </div>
        <Separator />

        {/* 2. Message text — comes BEFORE attachments per UX P1 (Apr 30). */}
        <div className="text-sm text-[#555555] leading-relaxed whitespace-pre-wrap" data-testid="email-detail-body">{email.body}</div>

        {/* 3. Stored Attachments — full row tappable, opens preview modal. */}
        {storedAtts.length > 0 && (
          <div className="space-y-1.5" data-testid="attachment-list">
            <p className="text-xs font-medium text-zinc-500 flex items-center gap-1.5">
              <Paperclip size={12} /> {strings.attachments.count(storedAtts.length)}
            </p>
            {storedAtts.map((att) => {
              const dlUrl = buildAttachmentUrl(att);
              return (
                <button
                  key={att.attachment_id}
                  type="button"
                  onClick={() => setPreviewAtt({ ...att, dlUrl })}
                  className="w-full flex items-center justify-between gap-2 bg-[#F5F5F5] rounded-lg px-3 py-2.5 border border-[#E5E5E5] hover:bg-[#EFEFEF] active:bg-[#E5E5E5] transition-colors text-left"
                  data-testid={`attachment-item-${att.attachment_id}`}
                  aria-label={`Open ${att.filename}`}
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FileText size={16} className="text-zinc-500 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-[#555555] truncate">{att.filename}</p>
                      <p className="text-[10px] text-zinc-500 font-mono">{formatSize(att.file_size)}</p>
                    </div>
                  </div>
                  <Eye size={14} className="text-zinc-500 shrink-0" />
                </button>
              );
            })}
          </div>
        )}

        {/* Rejected Attachments — warning */}
        {rejectedAtts.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 space-y-1.5" data-testid="rejected-attachments-warning">
            <p className="text-xs font-medium text-amber-800 flex items-center gap-1.5">
              <AlertTriangle size={12} />
              {rejectedAtts.length === 1
                ? strings.attachments.rejectedSingle
                : strings.attachments.rejectedMultiple(rejectedAtts.length)}
            </p>
            {rejectedAtts.map((att, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-amber-600">
                <X size={10} className="shrink-0" />
                <span className="font-mono truncate">{att.filename}</span>
                <span className="text-amber-500">—</span>
                <span>{reasonLabel(att.reason)}</span>
              </div>
            ))}
            <p className="text-[10px] text-amber-500 mt-1">{strings.attachments.allowedFormats}</p>
          </div>
        )}

        {/* 30-Minuten Ablauf-Hinweis */}
        <div className="mt-6 pt-4 border-t border-[#E5E5E5]" data-testid="free-detail-expiry-nudge">
          <div className="flex items-start gap-2.5">
            <AlertTriangle size={13} className="text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-[11px] text-zinc-500">
                {strings.emailDetail.downloadNow}
              </p>
              <Link to={upgradeUrl || "/register"} className="text-[11px] text-blue-500 hover:text-blue-600 font-medium transition-colors" data-testid="free-detail-upgrade-link">
                {strings.emailDetail.saveForever}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Attachment preview modal — opens when user taps an attachment row */}
      <AttachmentPreviewModal
        att={previewAtt}
        onClose={() => setPreviewAtt(null)}
      />
    </div>
  );
}

/**
 * AttachmentPreviewModal — opens on tap of any attachment row.
 * Renders an inline preview for image/PDF, a generic file icon for other
 * mime types, and always exposes a Download button + close (X).
 *
 * Auth piggybacks on the same `free_inbox_token` cookie used by the
 * download anchor, so iframes/img tags can fetch the file directly.
 */
function AttachmentPreviewModal({ att, onClose }) {
  const open = !!att;
  const isImage = (att?.mime_type || "").startsWith("image/");
  const isPdf = (att?.mime_type || "") === "application/pdf";
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async (e) => {
    if (!att?.dlUrl) return;
    e.preventDefault();
    e.stopPropagation();
    if (downloading) return;
    setDownloading(true);
    try {
      // shareOrDownloadFile handles iOS Safari (Share Sheet → blob fallback)
      // and surfaces a toast on failure. The modal stays open either way
      // so the user never gets ejected from the email detail view.
      await shareOrDownloadFile(att.dlUrl, att.filename, { mimeType: att.mime_type });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="sm:max-w-[640px] max-h-modal-vvh flex flex-col p-0 gap-0 overflow-hidden"
        data-testid="attachment-preview-modal"
        aria-describedby="attachment-preview-desc"
      >
        <DialogHeader className="px-4 py-3 border-b border-[#E5E5E5] shrink-0 text-left">
          <DialogTitle className="text-sm font-medium text-[#111111] truncate pr-8">
            {att?.filename || "Attachment"}
          </DialogTitle>
          <p id="attachment-preview-desc" className="text-[11px] text-zinc-500 font-mono">
            {att ? `${(att.mime_type || "unknown")} · ${(att.file_size != null ? (att.file_size < 1024 ? `${att.file_size} B` : att.file_size < 1024 * 1024 ? `${(att.file_size / 1024).toFixed(1)} KB` : `${(att.file_size / (1024 * 1024)).toFixed(1)} MB`) : "—")}` : ""}
          </p>
        </DialogHeader>
        <div className="flex-1 min-h-[200px] bg-[#F5F5F5] overflow-auto flex items-center justify-center p-3">
          {isImage && att?.dlUrl && (
            <img
              src={att.dlUrl}
              alt={att.filename}
              className="max-w-full max-h-[60vh] object-contain rounded"
              data-testid="attachment-preview-image"
            />
          )}
          {isPdf && att?.dlUrl && (
            <iframe
              src={att.dlUrl}
              title={att.filename}
              className="w-full h-[60vh] bg-white rounded border border-[#E5E5E5]"
              data-testid="attachment-preview-pdf"
            />
          )}
          {!isImage && !isPdf && (
            <div className="text-center py-8" data-testid="attachment-preview-generic">
              <FileText size={48} className="text-zinc-500 mx-auto mb-3" strokeWidth={1.25} />
              <p className="text-sm text-[#555555] font-medium truncate max-w-[300px]">{att?.filename}</p>
              <p className="text-xs text-zinc-500 mt-1">No inline preview available — tap Download.</p>
            </div>
          )}
        </div>
        <DialogFooter className="px-4 py-3 border-t border-[#E5E5E5] shrink-0 pb-kb">
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading || !att?.dlUrl}
            className="inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-md bg-zinc-900 hover:bg-zinc-800 disabled:opacity-60 text-white text-sm font-medium w-full sm:w-auto transition-colors"
            data-testid="attachment-preview-download"
          >
            {downloading
              ? <><Loader2 size={14} className="animate-spin" /> Downloading…</>
              : <><Download size={14} /> Download</>
            }
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
